import { Link } from "react-router-dom";
import { Check, Minus, Compass } from "lucide-react";
import { Prose, Unavailable, Method, CourseLink, capFirst } from "./Bits";
import { EvidenceChip } from "../EvidenceChip";
import { RankingPrototype } from "./RankingPrototype";
import { ProfileCta } from "./HeroWhat";

function FastMatchPanel({ fm }) {
  if (!fm?.taken) {
    return <p className="text-sm text-stone-700">You haven't completed Fast Match yet. It compares your interests and strengths with Philosophy's demands. <Link to="/beta/course-fit" data-testid="sci-fast-match-cta" className="font-medium text-[#002147] underline">Take Fast Match</Link></p>;
  }
  return fm.philosophy_rank ? (
    <p data-testid="sci-fast-match-result" className="text-sm text-stone-800">Your latest Fast Match put Philosophy at <strong>#{fm.philosophy_rank}</strong> in your top 5 (Fast Match display score {fm.philosophy_display_score}).</p>
  ) : (
    <p data-testid="sci-fast-match-result" className="text-sm text-stone-800">Philosophy wasn't in your latest Fast Match top 5{fm.top_subject ? ` (your top match was ${fm.top_subject})` : ""}. <Link to="/beta/course-fit" className="text-[#002147] underline">Retake</Link></p>
  );
}

export function WhoFits({ d }) {
  const fit = d.fit;
  return (
    <div className="space-y-12">
      <div className="grid items-start gap-10 lg:grid-cols-[1.4fr,1fr]">
        <div>
          <h3 className="mb-4 text-sm font-semibold text-stone-900">You'll probably enjoy it if…</h3>
          {d.editorial.who_traits.length ? <ul className="space-y-4">{d.editorial.who_traits.map((it, i) => (
            <li key={i} className="border-l-2 border-[#92400E] pl-4"><Prose item={it} testId={`sci-trait-${i}`} className="text-base leading-relaxed text-stone-800 md:text-lg" /></li>))}</ul>
            : <Unavailable testId="sci-traits-none">Not verified yet.</Unavailable>}
        </div>
      <aside className="h-fit rounded-2xl border border-stone-200 bg-white p-6" data-testid="sci-will-i-like-it">
        <div className="flex items-center gap-2 text-[#002147]"><Compass className="h-4 w-4" /><span className="font-mono text-[10px] uppercase tracking-[0.18em]">For you</span></div>
        <p className="mt-2 font-editorial text-2xl text-stone-900">Will I like it?</p>
        <div className="mt-4 space-y-3">
          <FastMatchPanel fm={fit.fast_match} />
          {!fit.subject.has_profile && <p className="text-sm text-stone-600">No profile yet, so there are no personal signals.</p>}
          {fit.subject.signals?.map((s, i) => (
            <p key={i} data-testid={`sci-subject-signal-${i}`} className="flex gap-2 text-sm text-stone-800">{s.kind === "match" ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" /> : <Minus className="mt-0.5 h-4 w-4 shrink-0 text-stone-400" />}{s.text}</p>))}
        </div>
        <p className="mt-4 text-xs text-stone-500">Signals come only from your saved Profile and Fast Match. We don't invent a fit score.</p>
        <div className="mt-4 flex flex-wrap gap-4"><ProfileCta /><Link to="/realisations" data-testid="sci-fit-subject-realisations" className="text-sm font-medium text-[#002147] underline underline-offset-2">Reflect in Realisations</Link></div>
      </aside>
      </div>
        <div>
          <h3 className="mb-4 text-sm font-semibold text-stone-900">What the degree trains you to do</h3>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">{d.editorial.who_skills.map((it, i) => (
            <li key={i} className="rounded-xl border border-stone-200 bg-white p-4"><Prose item={it} testId={`sci-skill-${i}`} className="text-sm leading-relaxed text-stone-800" /></li>))}</ul>
        </div>
      <div className="grid gap-10 lg:grid-cols-2">
        {!!d.aptitude?.dimensions?.length && (
          <div data-testid="sci-aptitude">
            <h3 className="mb-3 text-sm font-semibold text-stone-900">What Fast Match looks for in Philosophy</h3>
            <div className="flex flex-wrap gap-2">{d.aptitude.dimensions.map((x, i) => (
              <span key={i} title={x.definition} data-testid={`sci-aptitude-${i}`} className="rounded-full bg-violet-50 px-3 py-1 text-xs text-violet-900 ring-1 ring-violet-200">{x.name}</span>))}</div>
            <Method testId="sci-aptitude-method">WUA Fast Match subject profile {d.aptitude.version} ({d.aptitude.status}), calibrated on {d.aptitude.evidence.map((e) => e.publisher).join(" and ")}. Shown: dimensions with importance of 60 or more.</Method>
          </div>
        )}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-stone-900">School subjects</h3>
          <ul className="space-y-2">{d.required_subjects.slice(0, 5).map((c, i) => (
            <li key={i} data-testid={`sci-required-${i}`} className="flex items-start justify-between gap-3 text-sm text-stone-700"><span><span className="text-stone-500">{c.university}{c.context ? `, ${c.context}` : ""}:</span> {capFirst(c.value)}</span><EvidenceChip claim={c} subtle testId={`sci-required-${i}-source`} /></li>))}</ul>
        </div>
      </div>
    </div>
  );
}

export function Study({ d }) {
  return (
    <div className="space-y-12">
      <div>
        <h3 className="mb-4 text-sm font-semibold text-stone-900">What UK Philosophy degrees typically cover</h3>
        {d.editorial.study_topics.length ? <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{d.editorial.study_topics.map((it, i) => (
          <li key={i} data-testid={`sci-topic-${i}`} className="rounded-xl bg-[#002147] p-5 text-white">
            <p className="font-editorial text-xl">{it.title}</p>
            <Prose item={it} testId={`sci-topic-${i}-text`} className="mt-2 text-sm leading-relaxed text-white/85 [&_button]:text-white/60 [&_button:hover]:bg-white/10 [&_button:hover]:text-emerald-200" />
          </li>))}</ul>
          : <Unavailable testId="sci-topics-none">Not verified yet.</Unavailable>}
        <Method testId="sci-topics-method">From the QAA Subject Benchmark Statement for Philosophy, which describes UK honours degrees in general, not any one university's curriculum.</Method>
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        {[["How it's taught", d.editorial.study_teaching, "teach"], ["How it's assessed", d.editorial.study_assessment, "assess"]].map(([t, rows, k]) => (
          <div key={k}><h3 className="mb-3 text-sm font-semibold text-stone-900">{t}</h3>
            {rows.length ? <div className="space-y-3">{rows.map((it, i) => <Prose key={i} item={it} testId={`sci-${k}-${i}`} />)}</div> : <Unavailable testId={`sci-${k}-none`}>Not verified yet.</Unavailable>}</div>))}
      </div>
      <div>
        <h3 className="mb-4 text-sm font-semibold text-stone-900">How four real degrees progress</h3>
        <div className="grid gap-4 md:grid-cols-2">{d.modules.map((m) => (
          <div key={m.degree_id} data-testid={`sci-progression-${m.degree_id}`} className={`rounded-xl border p-5 ${m.joint ? "border-dashed border-amber-700/40 bg-amber-50/40" : "border-stone-200 bg-white"}`}>
            <p className="font-editorial text-lg text-stone-900"><CourseLink id={m.degree_id} testId={`sci-progression-link-${m.degree_id}`}>{m.university}</CourseLink></p>
            {m.joint && <p className="text-xs text-amber-900">Joint degree (PPE): Philosophy is one of three subjects, so it isn't typical single-honours Philosophy.</p>}
            <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-stone-500">Compulsory</p>
            <p className="text-sm text-stone-700">{m.compulsory.map((c) => capFirst(c.value)).join(" · ") || "Not verified"}</p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Example options</p>
            <p className="text-sm text-stone-700">{m.optional.map((c) => capFirst(c.value)).join(" · ") || "Not verified"}</p>
          </div>))}</div>
      </div>
    </div>
  );
}

export function Offered({ o }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[1fr,1fr]">
      <div data-testid="sci-offered-uk">
        <p className="font-mono text-5xl text-stone-900">{o.uk.universities}</p>
        <p className="text-sm text-stone-600">UK universities with at least one course with Philosophy in the title ({o.uk.courses} courses)</p>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div className="rounded-lg bg-white p-4 ring-1 ring-stone-200"><p className="font-mono text-2xl">{o.uk.single_honours_universities}</p><p className="text-stone-600">offer single-honours Philosophy ({o.uk.single_honours_courses} courses)</p></div>
          <div className="rounded-lg bg-white p-4 ring-1 ring-stone-200"><p className="font-mono text-2xl">{o.uk.joint_or_combined_courses}</p><p className="text-stone-600">joint or combined courses (e.g. PPE)</p></div>
        </div>
        <table className="mt-4 w-full text-sm" data-testid="sci-offered-awards"><tbody>{o.uk.awards.slice(0, 6).map((a) => (
          <tr key={a.award} className="border-b border-stone-100"><td className="py-1.5 text-stone-700">{a.award}</td><td className="py-1.5 text-right font-mono">{a.courses}</td></tr>))}</tbody></table>
        <Method testId="sci-offered-uk-method">{o.uk.method}</Method>
      </div>
      <div data-testid="sci-offered-world">
        <p className="font-mono text-5xl text-stone-900">{o.us.universities}</p>
        <p className="text-sm text-stone-600">US institutions with a bachelor's-level Philosophy record</p>
        <Method testId="sci-offered-us-method">{o.us.method}</Method>
        <h3 className="mb-2 mt-6 text-sm font-semibold text-stone-900">Elsewhere in our catalogue</h3>
        <p className="text-sm text-stone-700">{o.other.countries.map((c) => `${c.country} ${c.universities}`).join(" · ") || "None"}</p>
        <Method testId="sci-offered-other-method">{o.other.method}</Method>
      </div>
    </div>
  );
}

export function Rankings({ r }) {
  return (
    <div className="space-y-6">
      <p data-testid="sci-rankings-wua" className="max-w-3xl font-editorial text-xl leading-relaxed text-stone-800">The WUA Philosophy ranking is in development. We are building our own method from official open data, and we'll publish it with this guide once it's validated. Until then, courses are listed A to Z.</p>
      <RankingPrototype />
      <details className="max-w-3xl rounded-xl border border-dashed border-stone-300 bg-white p-4 text-sm" data-testid="sci-rankings-cug-reference">
        <summary className="cursor-pointer text-stone-700">Admin research reference: Complete University Guide Philosophy table {r.cug[0]?.entry_year || ""} (licence unconfirmed)</summary>
        <p className="mt-2 text-xs text-amber-900">This is for internal research only. It must not be shown to students, and it isn't used in any WUA ranking or score.</p>
        <ol className="mt-3 columns-1 gap-6 text-xs text-stone-700 sm:columns-2">{r.cug.map((c) => (
          <li key={c.value} data-testid={`sci-rank-${c.value}`} className="break-inside-avoid py-0.5">{c.value}. {c.context}<EvidenceChip claim={c} subtle testId={`sci-rank-${c.value}-source`} /></li>))}</ol>
      </details>
    </div>
  );
}
